// Frequency-type SVG icons.
//
// Maps the OurAirports / FAA frequency type code to a tiny inline SVG
// glyph that helps the pilot scan the frequency list at a glance.
// Icons are intentionally simple line art; we tint them via the
// surrounding text color so they match whatever color scheme the
// airport detail panel uses.
//
// Type codes commonly seen:
//   CTAF / UNICOM / MULTICOM   tower-less air-to-air
//   TWR / TOWER                control tower
//   GND / GROUND               ground control
//   CLNC / CLNC DEL            clearance delivery
//   ATIS                       Automatic Terminal Information Service
//   ASOS / AWOS                automated weather (also called WX)
//   APCH / APP / APPROACH      approach control
//   DEP / DEPARTURE            departure control
//   CNTR / CENTER              ARTCC / center
//   RDO / FSS                  Flight Service Station radio
//   ATF                        Aerodrome Traffic Frequency

import { Radio, RadioTower, Cloud, SatelliteDish, Plane, TowerControl, Headphones, Megaphone } from '../shell/Icon';

interface Props {
  type: string;
  className?: string;
}

/** Lucide doesn't have a literal "tower" or "ATIS" icon, so we map to the
 *  closest visual analogue. The idea is a quick visual scan, not a
 *  perfect taxonomy. */
export default function FrequencyIcon({ type, className = 'h-4 w-4' }: Props) {
  const t = (type || '').toUpperCase();

  // Match in priority order so 'TOWER' and 'TWR' both hit Tower, etc.
  if (t === 'TWR' || t === 'TOWER') {
    return <TowerControl className={className} aria-label="Tower" />;
  }
  if (t === 'GND' || t === 'GROUND') {
    return <Plane className={className} aria-label="Ground" />;
  }
  if (t === 'CLNC' || t.startsWith('CLNC') || t === 'CLEARANCE' || t.includes('DEL')) {
    return <Headphones className={className} aria-label="Clearance" />;
  }
  if (t === 'ATIS') {
    return <Megaphone className={className} aria-label="ATIS" />;
  }
  if (t === 'ASOS' || t === 'AWOS' || t === 'WX' || t === 'WEATHER') {
    return <Cloud className={className} aria-label="Automated weather" />;
  }
  if (t === 'CTAF' || t === 'UNICOM' || t === 'UNIC' || t === 'MULTICOM' || t === 'ATF') {
    return <Radio className={className} aria-label="CTAF/UNICOM" />;
  }
  if (t === 'APP' || t === 'APCH' || t === 'APPROACH' || t === 'DEP' || t === 'DEPARTURE') {
    return <SatelliteDish className={className} aria-label="Approach/Departure" />;
  }
  if (t === 'CNTR' || t === 'CENTER' || t === 'ARTCC') {
    return <RadioTower className={className} aria-label="Center" />;
  }
  if (t === 'RDO' || t === 'FSS') {
    return <Radio className={className} aria-label="Flight Service" />;
  }
  // Fallback: generic radio icon.
  return <Radio className={className} aria-label={type} />;
}
